/** Game setup choices for a new project: 2D or 3D first, then everything else. */

export type Dimension = '2d' | '3d';

export interface GameSetup {
  dimension: Dimension;
  template: string;
  brief: string;
}

interface Choice {
  value: string;
  label: string;
  hint: string;
}

const TEMPLATES: Record<Dimension, Choice[]> = {
  '2d': [
    { value: 'empty-2d', label: 'Empty 2D', hint: 'A 2D camera and nothing else' },
    { value: 'platformer-2d', label: 'Platformer', hint: 'Side view: run, jump, coins, pixel-art tile level' },
    { value: 'topdown-2d', label: 'Top-down', hint: 'Pixel-art dungeon rooms, 8-way movement, walls' },
  ],
  '3d': [{ value: 'empty', label: 'Empty 3D', hint: 'Sky, light, ground and a camera' }],
};

/** Neutral starter selected when a dimension is chosen. */
const DEFAULT_TEMPLATE: Record<Dimension, string> = { '2d': 'empty-2d', '3d': 'empty' };

export function defaultSetup(dimension: Dimension = '3d'): GameSetup {
  return {
    dimension,
    template: DEFAULT_TEMPLATE[dimension],
    brief: '',
  };
}

function ChoiceGroup(props: {
  label: string;
  choices: Choice[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <fieldset className="choice-field">
      <legend>{props.label}</legend>
      <div className="choice-grid" role="radiogroup" aria-label={props.label}>
        {props.choices.map((c) => (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={props.value === c.value}
            className={`choice${props.value === c.value ? ' choice-on' : ''}`}
            onClick={() => props.onChange(c.value)}
          >
            <strong>{c.label}</strong>
            <span className="muted">{c.hint}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export function NewGameOptions(props: { setup: GameSetup; onChange: (setup: GameSetup) => void }) {
  const { setup, onChange } = props;
  return (
    <div className="new-game">
      <ChoiceGroup
        label="Game type"
        choices={[
          { value: '2d', label: '2D', hint: 'Sprites, tilemaps, 2D physics' },
          { value: '3d', label: '3D', hint: 'Models, terrain, lighting' },
        ]}
        value={setup.dimension}
        onChange={(d) =>
          onChange({ ...defaultSetup(d as Dimension), brief: setup.brief })
        }
      />
      <ChoiceGroup
        label="Starter"
        choices={TEMPLATES[setup.dimension]}
        value={setup.template}
        onChange={(template) => onChange({ ...setup, template })}
      />
      <label className="field">
        <span>Describe the game (optional; the AI reads this)</span>
        <textarea
          rows={3}
          value={setup.brief}
          onChange={(e) => onChange({ ...setup, brief: e.target.value })}
          placeholder="A cozy farming game where you befriend forest spirits…"
        />
      </label>
    </div>
  );
}
